import React, { useState, useEffect } from 'react';
import { UserLayout } from '../../components/user/UserLayout';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Receipt, Save, Loader2, Ban, ChevronUp, User, Calendar, Armchair, Users, Baby } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const CANCELLATION_REASONS = ['Cancelled', 'Comp', 'Void', 'Gesture'];

const DISCOUNT_CATEGORIES = [
  'General Discount',
  'IHG Discount',
  'Industry Discount',
  'Special Discount',
];

// Property-admin viewer only — these internal sections are never shown to customers
const isPropertyAdminViewer = () => {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) return false;
    const u = JSON.parse(raw);
    return (u?.entityType || '').toLowerCase() === 'property';
  } catch {
    return false;
  }
};

export const AllocationBilling = () => {
  const { allocationId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allocation, setAllocation] = useState(null);
  const [seats, setSeats] = useState([]);
  const [staff, setStaff] = useState([]);
  const [items, setItems] = useState([]);
  const [overallDiscountType, setOverallDiscountType] = useState('none');
  const [overallDiscountValue, setOverallDiscountValue] = useState(0);
  const [overallDiscountCategory, setOverallDiscountCategory] = useState('');
  // Bulk discount panel state
  const [bulkDiscountCategory, setBulkDiscountCategory] = useState('');
  const [bulkDiscountType, setBulkDiscountType] = useState('percentage');
  const [bulkDiscountValue, setBulkDiscountValue] = useState('');
  const [cancellingIdx, setCancellingIdx] = useState(null);
  const [cancelForm, setCancelForm] = useState({ reason: '', description: '' });

  useEffect(() => {
    fetchBillingData();
  }, [allocationId]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const userData = localStorage.getItem('userData');
      const propertyId = userData ? JSON.parse(userData).entityId : null;

      const requests = [
        axios.get(`${BACKEND_URL}/api/allocations/${allocationId}`),
        axios.get(`${BACKEND_URL}/api/billing/${allocationId}`)
      ];
      if (propertyId) {
        requests.push(
          axios.get(`${BACKEND_URL}/api/seats/${propertyId}`),
          axios.get(`${BACKEND_URL}/api/staff/${propertyId}`)
        );
      }

      const results = await Promise.all(requests);
      const [allocRes, billRes] = results;

      if (allocRes.data.success) {
        setAllocation(allocRes.data.allocation);
      }
      if (results[2]?.data?.success) setSeats(results[2].data.seats || []);
      if (results[3]?.data?.success) setStaff(results[3].data.staff || []);

      if (billRes.data.code === 200) {
        const bill = billRes.data.data;
        setItems(bill.items.map(item => ({
          ...item,
          discountType: item.discountType || 'none',
          discountValue: item.discountValue || 0,
          discountCategory: item.discountCategory || '',
          orderType: item.orderType || 'Kitchen',
          cancelled: item.cancelled || false,
          cancellationReason: item.cancellationReason || '',
          cancellationDescription: item.cancellationDescription || '',
          costInvolved: typeof item.costInvolved === 'boolean' ? item.costInvolved : null,
          itemWasMade: typeof item.itemWasMade === 'boolean' ? item.itemWasMade : null,
          cancelledAtStatus: item.cancelledAtStatus || null,
          orderId: item.orderId || null,
          source: item.source || null,
        })));
        setOverallDiscountType(bill.overallDiscountType || 'none');
        setOverallDiscountValue(bill.overallDiscountValue || 0);
        setOverallDiscountCategory(bill.overallDiscountCategory || '');
      }
    } catch (error) {
      console.error('Error fetching billing data:', error);
      toast({ title: 'Error', description: 'Failed to load billing data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Helper functions to resolve names
  const getSeatNumbers = () => {
    if (!allocation?.seatIds?.length || !seats.length) return 'N/A';
    return allocation.seatIds.map(id => { const s = seats.find(x => x.id === id); return s ? s.seatNumber : id; }).join(', ');
  };
  const getStaffName = (id) => { const s = staff.find(x => x.id === id); return s ? s.name : 'N/A'; };
  const getStaffNames = (ids) => {
    if (!ids?.length || !staff.length) return [];
    return ids.map(id => { const s = staff.find(x => x.id === id); return s ? s.name : null; }).filter(Boolean);
  };

  const calcItemTotal = (item) => {
    if (item.cancelled) return 0;
    const base = item.unitPrice * item.quantity;
    if (item.discountType === 'percentage') return Math.max(base * (1 - item.discountValue / 100), 0);
    if (item.discountType === 'fixed') return Math.max(base - item.discountValue, 0);
    return base;
  };

  const activeItems = items.filter(it => !it.cancelled);
  const cancelledItems = items.filter(it => it.cancelled);
  const costInvolvedItems = items.filter(it => it.cancelled && it.costInvolved === true);
  const costInvolvedTotal = costInvolvedItems.reduce(
    (sum, it) => sum + (Number(it.unitPrice) || 0) * (Number(it.quantity) || 0),
    0
  );
  const showInternalSections = isPropertyAdminViewer();

  const subtotal = activeItems.reduce((sum, item) => sum + calcItemTotal(item), 0);

  const overallDiscountAmount = overallDiscountType === 'percentage'
    ? subtotal * overallDiscountValue / 100
    : overallDiscountType === 'fixed'
      ? Math.min(overallDiscountValue, subtotal)
      : 0;

  const grandTotal = Math.max(subtotal - overallDiscountAmount, 0);

  const updateItemDiscount = (index, field, value) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      const updated = { ...item, [field]: value };
      if (field === 'discountType' && value === 'none') {
        updated.discountValue = 0;
        updated.discountCategory = '';
      }
      return updated;
    }));
  };

  // Bulk apply a discount to all active items matching the target order type
  // target: 'Kitchen' | 'Drink' | 'All'
  const bulkApplyDiscount = (target) => {
    if (!bulkDiscountCategory) {
      toast({ title: 'Required', description: 'Please choose a discount type first.', variant: 'destructive' });
      return;
    }
    const raw = parseFloat(bulkDiscountValue);
    if (Number.isNaN(raw) || raw <= 0) {
      toast({ title: 'Required', description: 'Enter a positive discount value.', variant: 'destructive' });
      return;
    }
    if (bulkDiscountType === 'percentage' && raw > 100) {
      toast({ title: 'Invalid', description: 'Percentage discount cannot exceed 100%.', variant: 'destructive' });
      return;
    }

    let touched = 0;
    setItems(prev => prev.map(item => {
      if (item.cancelled) return item;
      const itemType = item.orderType || 'Kitchen';
      const matches = target === 'All'
        || (target === 'Kitchen' && itemType === 'Kitchen')
        || (target === 'Drink' && itemType === 'Drink');
      if (!matches) return item;
      touched += 1;
      return {
        ...item,
        discountType: bulkDiscountType,
        discountValue: raw,
        discountCategory: bulkDiscountCategory,
      };
    }));

    const label = target === 'All' ? 'all active items' : target === 'Drink' ? 'bar items' : 'kitchen items';
    toast({
      title: touched > 0 ? 'Discount applied' : 'No items matched',
      description: touched > 0
        ? `${bulkDiscountCategory} (${bulkDiscountType === 'percentage' ? raw + '%' : '$' + raw}) applied to ${touched} ${label}.`
        : `No active ${label} found.`,
      variant: touched > 0 ? 'default' : 'destructive',
    });
  };

  const clearAllDiscounts = () => {
    setItems(prev => prev.map(item => item.cancelled
      ? item
      : { ...item, discountType: 'none', discountValue: 0, discountCategory: '' }
    ));
    toast({ title: 'Cleared', description: 'Per-item discounts removed from active items.' });
  };

  const openCancelForm = (idx) => {
    const item = items[idx];
    setCancellingIdx(idx);
    setCancelForm({
      reason: item.cancellationReason || '',
      description: item.cancellationDescription || ''
    });
  };

  const confirmCancel = () => {
    if (!cancelForm.reason) {
      toast({ title: 'Required', description: 'Please select a cancellation reason', variant: 'destructive' });
      return;
    }
    if (!cancelForm.description.trim()) {
      toast({ title: 'Required', description: 'Please provide a cancellation description', variant: 'destructive' });
      return;
    }
    setItems(prev => prev.map((item, i) => {
      if (i !== cancellingIdx) return item;
      return {
        ...item,
        cancelled: true,
        cancellationReason: cancelForm.reason,
        cancellationDescription: cancelForm.description.trim(),
        discountType: 'none',
        discountValue: 0,
      };
    }));
    setCancellingIdx(null);
    setCancelForm({ reason: '', description: '' });
  };

  const restoreItem = (idx) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      return { ...item, cancelled: false, cancellationReason: '', cancellationDescription: '' };
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        items: items.map(item => ({
          menuItemId: item.menuItemId,
          menuItemName: item.menuItemName,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discountType: item.discountType,
          discountValue: Number(item.discountValue) || 0,
          discountedTotal: calcItemTotal(item),
          discountCategory: item.discountCategory || null,
          orderType: item.orderType || 'Kitchen',
          notes: item.notes || null,
          cancelled: item.cancelled || false,
          cancellationReason: item.cancellationReason || null,
          cancellationDescription: item.cancellationDescription || null,
          // Carry-over fields for order-side cancellations
          costInvolved: typeof item.costInvolved === 'boolean' ? item.costInvolved : null,
          itemWasMade: typeof item.itemWasMade === 'boolean' ? item.itemWasMade : null,
          cancelledAtStatus: item.cancelledAtStatus || null,
          orderId: item.orderId || null,
          source: item.source || null,
        })),
        overallDiscountType,
        overallDiscountValue: Number(overallDiscountValue) || 0,
        overallDiscountCategory: overallDiscountCategory || null,
      };

      const response = await axios.post(`${BACKEND_URL}/api/billing/${allocationId}`, payload);
      if (response.data.code === 200) {
        toast({ title: 'Saved', description: 'Bill saved successfully' });
      } else {
        toast({ title: 'Error', description: response.data.message || 'Failed to save bill', variant: 'destructive' });
      }
    } catch (error) {
      console.error('Error saving bill:', error);
      toast({ title: 'Error', description: 'Failed to save bill', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const renderItemRow = (item, idx, isCancelled) => {
    const baseTotal = item.unitPrice * item.quantity;
    const discountedTotal = calcItemTotal(item);
    const hasDiscount = !isCancelled && item.discountType !== 'none' && item.discountValue > 0;

    return (
      <tr key={idx} className={isCancelled ? 'bg-red-50/50' : ''} data-testid={`bill-item-row-${idx}`}>
        <td className="px-6 py-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-medium ${isCancelled ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{item.menuItemName}</span>
            {!isCancelled && (
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                (item.orderType || 'Kitchen') === 'Drink' ? 'bg-cyan-100 text-cyan-700' : 'bg-orange-100 text-orange-700'
              }`}>
                {(item.orderType || 'Kitchen') === 'Drink' ? 'Bar' : 'Kitchen'}
              </span>
            )}
            {!isCancelled && hasDiscount && item.discountCategory && (
              <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-teal-100 text-teal-700">
                {item.discountCategory}
              </span>
            )}
          </div>
          {item.notes && !isCancelled && (
            <p className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded mt-1 italic inline-block">{item.notes}</p>
          )}
          {isCancelled && (
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-100 text-red-700">{item.cancellationReason}</span>
              {item.costInvolved === true && (
                <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-200 text-rose-800" title="Internal: cost was involved">Cost</span>
              )}
              {item.costInvolved === false && (
                <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-200 text-slate-600" title="Internal: no cost involved">No Cost</span>
              )}
              {item.cancelledAtStatus && (
                <span className="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" title="Order status at cancellation">@ {item.cancelledAtStatus}</span>
              )}
              {item.source === 'order' && item.orderId && (
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-50 text-slate-500" title="Source order">#{item.orderId}</span>
              )}
              {item.cancellationDescription && (
                <span className="text-xs text-slate-400 italic w-full">{item.cancellationDescription}</span>
              )}
            </div>
          )}
        </td>
        <td className={`px-4 py-4 text-center ${isCancelled ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{item.quantity}</td>
        <td className={`px-4 py-4 text-right ${isCancelled ? 'text-slate-400 line-through' : 'text-slate-600'}`}>${item.unitPrice.toFixed(2)}</td>
        <td className="px-4 py-4">
          {isCancelled ? (
            <div className="text-center text-xs text-red-500 font-medium">—</div>
          ) : (
            <div className="flex flex-col items-center gap-1.5">
              <div className="flex items-center gap-1">
                <select
                  value={item.discountType}
                  onChange={(e) => updateItemDiscount(idx, 'discountType', e.target.value)}
                  className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-teal-400"
                  data-testid={`bill-item-discount-type-${idx}`}
                >
                  <option value="none">None</option>
                  <option value="percentage">%</option>
                  <option value="fixed">$</option>
                </select>
                {item.discountType !== 'none' && (
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={item.discountValue || ''}
                    onChange={(e) => updateItemDiscount(idx, 'discountValue', parseFloat(e.target.value) || 0)}
                    className="w-20 text-xs border border-slate-200 rounded-md px-2 py-1.5 text-right focus:outline-none focus:ring-1 focus:ring-teal-400"
                    data-testid={`bill-item-discount-value-${idx}`}
                  />
                )}
              </div>
              {item.discountType !== 'none' && (
                <select
                  value={item.discountCategory || ''}
                  onChange={(e) => updateItemDiscount(idx, 'discountCategory', e.target.value)}
                  className={`text-[11px] border rounded-md px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-teal-400 ${
                    item.discountCategory ? 'border-teal-300 text-teal-700' : 'border-amber-300 text-amber-700'
                  }`}
                  data-testid={`bill-item-discount-category-${idx}`}
                >
                  <option value="">Discount type…</option>
                  {DISCOUNT_CATEGORIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              )}
            </div>
          )}
        </td>
        <td className="px-4 py-4 text-right">
          {isCancelled ? (
            <span className="text-slate-400 line-through">${baseTotal.toFixed(2)}</span>
          ) : (
            <>
              {hasDiscount && <span className="text-xs text-slate-400 line-through mr-2">${baseTotal.toFixed(2)}</span>}
              <span className="font-semibold text-slate-800">${discountedTotal.toFixed(2)}</span>
            </>
          )}
        </td>
        <td className="px-4 py-4 text-center">
          {isCancelled ? (
            item.source === 'order' ? (
              <span
                className="text-xs text-slate-400 italic"
                title="This item was cancelled from the Orders kanban and can't be restored here."
              >
                From order
              </span>
            ) : (
              <button
                onClick={() => restoreItem(idx)}
                className="text-xs text-teal-600 hover:text-teal-800 font-medium hover:underline"
                data-testid={`bill-item-restore-${idx}`}
              >
                Restore
              </button>
            )
          ) : (
            <button
              onClick={() => openCancelForm(idx)}
              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              data-testid={`bill-item-cancel-${idx}`}
              title="Cancel item"
            >
              <Ban className="w-4 h-4" />
            </button>
          )}
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate(`/user/allocation/${allocationId}`)} data-testid="bill-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2" data-testid="bill-page-title">
                <Receipt className="w-6 h-6 text-teal-500" />
                Bill
              </h1>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving || items.length === 0} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-bill-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Bill
          </Button>
        </div>

        {/* Allocation Details Card */}
        {allocation && (
          <div className="bg-white rounded-xl shadow-md p-6" data-testid="bill-allocation-details">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-5">
              <div className="flex items-start gap-3">
                <User className="w-4 h-4 text-blue-500 mt-1 shrink-0" />
                <div>
                  <p className="text-xs text-slate-500">Guest Name</p>
                  <p className="text-sm font-semibold text-slate-800">{allocation.guestName}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-purple-500 mt-1 shrink-0" />
                <div>
                  <p className="text-xs text-slate-500">Room Number</p>
                  <p className="text-sm font-semibold text-slate-800">{allocation.roomNumber}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Armchair className="w-4 h-4 text-green-500 mt-1 shrink-0" />
                <div>
                  <p className="text-xs text-slate-500">Seats</p>
                  <p className="text-sm font-semibold text-slate-800">{getSeatNumbers()}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <User className="w-4 h-4 text-orange-500 mt-1 shrink-0" />
                <div>
                  <p className="text-xs text-slate-500">F&B Manager</p>
                  <p className="text-sm font-semibold text-slate-800">{getStaffName(allocation.fbManagerId)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-teal-500 mt-1 shrink-0" />
                <div>
                  <p className="text-xs text-slate-500">Allocation Date</p>
                  <p className="text-sm font-semibold text-slate-800">
                    {new Date(allocation.createdAt).toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users className="w-4 h-4 text-indigo-500 mt-1 shrink-0" />
                <div>
                  <p className="text-xs text-slate-500">Number of Adults</p>
                  <p className="text-sm font-semibold text-slate-800">{allocation.numAdults || 0}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Baby className="w-4 h-4 text-pink-500 mt-1 shrink-0" />
                <div>
                  <p className="text-xs text-slate-500">Number of Kids</p>
                  <p className="text-sm font-semibold text-slate-800">{allocation.numKids || 0}</p>
                </div>
              </div>
              {getStaffNames(allocation.poolBeachAttendantIds).length > 0 && (
                <div className="flex items-start gap-3">
                  <User className="w-4 h-4 text-cyan-500 mt-1 shrink-0" />
                  <div>
                    <p className="text-xs text-slate-500">Pool And Beach Attendant</p>
                    <p className="text-sm font-semibold text-slate-800">{getStaffNames(allocation.poolBeachAttendantIds).join(', ')}</p>
                  </div>
                </div>
              )}
              {getStaffNames(allocation.fbServerIds).length > 0 && (
                <div className="flex items-start gap-3">
                  <User className="w-4 h-4 text-violet-500 mt-1 shrink-0" />
                  <div>
                    <p className="text-xs text-slate-500">Pool And Beach Server</p>
                    <p className="text-sm font-semibold text-slate-800">{getStaffNames(allocation.fbServerIds).join(', ')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {items.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <Receipt className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">No items to bill</h2>
            <p className="text-slate-500">No delivered or active orders found for this allocation.</p>
          </div>
        ) : (
          <>
            {/* Bulk Discount Panel */}
            <div className="bg-white rounded-xl shadow-md p-5 border border-teal-100" data-testid="bulk-discount-panel">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-slate-800">Apply Discount in Bulk</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Pick a discount type & value, then apply it to all kitchen items, all bar items, or everything in one click.</p>
                </div>
                <button
                  onClick={clearAllDiscounts}
                  className="text-xs text-slate-500 hover:text-slate-800 underline"
                  data-testid="clear-all-discounts-btn"
                >
                  Clear all item discounts
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-4">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Discount Type *</label>
                  <select
                    value={bulkDiscountCategory}
                    onChange={(e) => setBulkDiscountCategory(e.target.value)}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
                    data-testid="bulk-discount-category"
                  >
                    <option value="">Select…</option>
                    {DISCOUNT_CATEGORIES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Mode</label>
                  <select
                    value={bulkDiscountType}
                    onChange={(e) => setBulkDiscountType(e.target.value)}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
                    data-testid="bulk-discount-mode"
                  >
                    <option value="percentage">% Percent</option>
                    <option value="fixed">$ Fixed</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Value</label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    placeholder={bulkDiscountType === 'percentage' ? 'e.g. 10' : 'e.g. 5.00'}
                    value={bulkDiscountValue}
                    onChange={(e) => setBulkDiscountValue(e.target.value)}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-right focus:outline-none focus:ring-2 focus:ring-teal-400"
                    data-testid="bulk-discount-value"
                  />
                </div>
                <div className="md:col-span-4 flex flex-wrap gap-2 justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => bulkApplyDiscount('Kitchen')}
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    data-testid="apply-discount-kitchen"
                  >
                    Apply to Kitchen
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => bulkApplyDiscount('Drink')}
                    className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                    data-testid="apply-discount-bar"
                  >
                    Apply to Bar
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => bulkApplyDiscount('All')}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                    data-testid="apply-discount-all"
                  >
                    Apply to All
                  </Button>
                </div>
              </div>
            </div>

            {/* Cancellation Dialog - inline below table */}
            {cancellingIdx !== null && (
              <div className="bg-white rounded-xl shadow-lg border-2 border-red-200 p-6" data-testid="cancel-item-dialog">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <Ban className="w-5 h-5 text-red-500" />
                      Cancel Item: {items[cancellingIdx]?.menuItemName}
                    </h3>
                    <p className="text-sm text-slate-500 mt-0.5">
                      {items[cancellingIdx]?.quantity} x ${items[cancellingIdx]?.unitPrice.toFixed(2)}
                    </p>
                  </div>
                  <button onClick={() => { setCancellingIdx(null); setCancelForm({ reason: '', description: '' }); }} className="p-1 text-slate-400 hover:text-slate-600">
                    <ChevronUp className="w-5 h-5" />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Reason *</label>
                    <select
                      value={cancelForm.reason}
                      onChange={(e) => setCancelForm(prev => ({ ...prev, reason: e.target.value }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 bg-white"
                      data-testid="cancel-reason-select"
                    >
                      <option value="">Select reason...</option>
                      {CANCELLATION_REASONS.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Description *</label>
                    <input
                      type="text"
                      value={cancelForm.description}
                      onChange={(e) => setCancelForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Reason for cancellation..."
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                      data-testid="cancel-description-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setCancellingIdx(null); setCancelForm({ reason: '', description: '' }); }}
                  >
                    Go Back
                  </Button>
                  <Button
                    size="sm"
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={confirmCancel}
                    data-testid="confirm-cancel-btn"
                  >
                    <Ban className="w-3.5 h-3.5 mr-1.5" />
                    Confirm Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Active Items Table */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden" data-testid="bill-items-table">
              <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <h2 className="font-semibold text-slate-700">Order Items ({activeItems.length})</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left bg-slate-50/50">
                      <th className="px-6 py-3 font-medium text-slate-500">Item</th>
                      <th className="px-4 py-3 font-medium text-slate-500 text-center">Qty</th>
                      <th className="px-4 py-3 font-medium text-slate-500 text-right">Unit Price</th>
                      <th className="px-4 py-3 font-medium text-slate-500 text-center">Discount</th>
                      <th className="px-4 py-3 font-medium text-slate-500 text-right">Total</th>
                      <th className="px-4 py-3 font-medium text-slate-500 text-center w-20"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((item, idx) => !item.cancelled && renderItemRow(item, idx, false))}
                  </tbody>
                </table>
              </div>
              {activeItems.length === 0 && (
                <div className="text-center py-8 text-slate-400 text-sm">All items cancelled</div>
              )}
            </div>

            {/* Cancelled Items (internal view — property admin only) */}
            {showInternalSections && cancelledItems.length > 0 && (
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-red-100" data-testid="bill-cancelled-items">
                <div className="px-6 py-4 border-b border-red-100 bg-red-50 flex items-center justify-between">
                  <h2 className="font-semibold text-red-700 flex items-center gap-2">
                    <Ban className="w-4 h-4" />
                    Cancelled Items ({cancelledItems.length})
                  </h2>
                  <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-slate-200 text-slate-600">Internal</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-red-100 text-left bg-red-50/30">
                        <th className="px-6 py-3 font-medium text-slate-500">Item</th>
                        <th className="px-4 py-3 font-medium text-slate-500 text-center">Qty</th>
                        <th className="px-4 py-3 font-medium text-slate-500 text-right">Unit Price</th>
                        <th className="px-4 py-3 font-medium text-slate-500 text-center">Discount</th>
                        <th className="px-4 py-3 font-medium text-slate-500 text-right">Total</th>
                        <th className="px-4 py-3 font-medium text-slate-500 text-center w-20"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-50">
                      {items.map((item, idx) => item.cancelled && renderItemRow(item, idx, true))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Cancelled — Cost Involved (internal financial leakage tracker — property admin only) */}
            {showInternalSections && costInvolvedItems.length > 0 && (
              <div className="bg-white rounded-xl shadow-md overflow-hidden border-2 border-rose-200" data-testid="bill-cost-involved-cancellations">
                <div className="px-6 py-4 border-b border-rose-200 bg-gradient-to-r from-rose-50 to-amber-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Ban className="w-4 h-4 text-rose-600" />
                    <div>
                      <h2 className="font-semibold text-rose-700">Cancelled — Cost Involved ({costInvolvedItems.length})</h2>
                      <p className="text-xs text-rose-600/80">Internal — items the hotel paid for but the guest wasn't charged. Tracks unnecessary expense.</p>
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-rose-200 text-rose-700">Internal</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-rose-100 text-left bg-rose-50/30">
                        <th className="px-6 py-3 font-medium text-slate-500">Item</th>
                        <th className="px-4 py-3 font-medium text-slate-500 text-center">Qty</th>
                        <th className="px-4 py-3 font-medium text-slate-500 text-right">Unit Price</th>
                        <th className="px-4 py-3 font-medium text-slate-500 text-center">Reason</th>
                        <th className="px-4 py-3 font-medium text-slate-500 text-right">Lost Cost</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-rose-50">
                      {costInvolvedItems.map((item, i) => {
                        const lost = (Number(item.unitPrice) || 0) * (Number(item.quantity) || 0);
                        return (
                          <tr key={`ci-${i}`} data-testid={`cost-involved-row-${i}`}>
                            <td className="px-6 py-3">
                              <p className="font-medium text-slate-800">{item.menuItemName}</p>
                              {item.cancellationDescription && (
                                <p className="text-xs text-slate-500 italic mt-0.5">{item.cancellationDescription}</p>
                              )}
                              <div className="flex flex-wrap gap-1.5 mt-1">
                                {item.cancelledAtStatus && (
                                  <span className="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">@ {item.cancelledAtStatus}</span>
                                )}
                                {item.itemWasMade === true && (
                                  <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Was made</span>
                                )}
                                {item.source === 'order' && item.orderId && (
                                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-50 text-slate-500">#{item.orderId}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center text-slate-700">{item.quantity}</td>
                            <td className="px-4 py-3 text-right text-slate-600">${Number(item.unitPrice).toFixed(2)}</td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-100 text-red-700">{item.cancellationReason}</span>
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-rose-700">${lost.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-rose-200 bg-rose-50">
                        <td colSpan={4} className="px-6 py-3 text-right font-semibold text-rose-700">Total Unnecessary Expense</td>
                        <td className="px-4 py-3 text-right text-lg font-bold text-rose-700" data-testid="cost-involved-total">${costInvolvedTotal.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="bg-white rounded-xl shadow-md p-6" data-testid="bill-summary">
              <div className="space-y-4 max-w-md ml-auto">
                {/* Subtotal */}
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal</span>
                  <span className="font-medium">${subtotal.toFixed(2)}</span>
                </div>

                {/* Overall Discount */}
                <div className="border-t border-slate-100 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-600">Overall Discount</span>
                    <div className="flex items-center gap-2">
                      <select
                        value={overallDiscountType}
                        onChange={(e) => {
                          setOverallDiscountType(e.target.value);
                          if (e.target.value === 'none') {
                            setOverallDiscountValue(0);
                            setOverallDiscountCategory('');
                          }
                        }}
                        className="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-teal-400"
                        data-testid="bill-overall-discount-type"
                      >
                        <option value="none">None</option>
                        <option value="percentage">%</option>
                        <option value="fixed">$</option>
                      </select>
                      {overallDiscountType !== 'none' && (
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={overallDiscountValue || ''}
                          onChange={(e) => setOverallDiscountValue(parseFloat(e.target.value) || 0)}
                          className="w-24 text-sm border border-slate-200 rounded-md px-2 py-1.5 text-right focus:outline-none focus:ring-1 focus:ring-teal-400"
                          data-testid="bill-overall-discount-value"
                        />
                      )}
                    </div>
                  </div>
                  {overallDiscountType !== 'none' && (
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-slate-500">Discount Type</span>
                      <select
                        value={overallDiscountCategory}
                        onChange={(e) => setOverallDiscountCategory(e.target.value)}
                        className={`text-xs border rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-teal-400 ${
                          overallDiscountCategory ? 'border-teal-300 text-teal-700' : 'border-amber-300 text-amber-700'
                        }`}
                        data-testid="bill-overall-discount-category"
                      >
                        <option value="">Select type…</option>
                        {DISCOUNT_CATEGORIES.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {overallDiscountAmount > 0 && (
                    <div className="flex justify-between text-red-600 text-sm">
                      <span>Discount {overallDiscountCategory ? `(${overallDiscountCategory})` : ''}</span>
                      <span>-${overallDiscountAmount.toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {/* Grand Total */}
                <div className="border-t-2 border-slate-800 pt-4">
                  <div className="flex justify-between">
                    <span className="text-lg font-bold text-slate-800">Grand Total</span>
                    <span className="text-2xl font-bold text-slate-800" data-testid="bill-grand-total">${grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </UserLayout>
  );
};
